<script setup lang="ts">
import { computed, inject, ref, useAttrs, useSlots } from 'vue'
import { FieldContextKey } from './fieldContext'
import {
  CONTROL_HEIGHT,
  CONTROL_SIZE,
  controlClasses,
  setsDimension,
  type ControlSize,
} from './controlStyles'

defineOptions({ inheritAttrs: false })

interface Props {
  modelValue?: string | number | null
  type?: string
  size?: ControlSize
  /** Force the error styling. Inferred from a surrounding FormField when omitted. */
  invalid?: boolean
  id?: string
  /** Populated by Vue for v-model.number / .trim; a component must apply them itself. */
  modelModifiers?: Record<string, boolean>
}

/**
 * `invalid` defaults to undefined on purpose. Vue casts an absent Boolean prop
 * to `false`, which would win the `??` below and stop a surrounding FormField
 * from ever supplying the error state. An explicit default suppresses that cast.
 */
const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  size: 'md',
  invalid: undefined,
  modelModifiers: () => ({}),
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
}>()

const slots = useSlots()
const attrs = useAttrs()
const field = inject(FieldContextKey, null)

const isInvalid = computed(() => props.invalid ?? field?.invalid.value ?? false)
const controlId = computed(() => props.id ?? field?.id.value)

/**
 * `color` and `range` are not text fields — the browser renders its own widget,
 * so the text-field styling actively breaks them: a colour swatch stretched to
 * w-full and 38px tall, a slider 38px instead of 2px. They get no layout from
 * the primitive at all; the caller sizes them and BaseInput just does the
 * v-model, id and aria plumbing.
 */
const BARE_TYPES = new Set(['color', 'range'])
const isBare = computed(() => BARE_TYPES.has(props.type))
const BARE_CONTROL = 'disabled:opacity-50 disabled:cursor-not-allowed'

/** Yield to a width or height the caller set — see controlClasses. */
const ownWidth = computed(() => setsDimension(attrs.class, 'w-'))
const ownHeight = computed(() => setsDimension(attrs.class, 'h-'))

/** A currency or unit affix turns the control into a bordered group. */
const hasAffix = computed(() => !!slots.prefix || !!slots.suffix)

const groupClasses = computed(() => [
  'flex overflow-hidden rounded-md border transition-colors',
  // The group carries the height so an affix field lines up with a plain one
  // on the same row — they used to differ because padding decided the height.
  ownHeight.value ? '' : CONTROL_HEIGHT[props.size],
  ownWidth.value ? '' : 'w-full',
  'focus-within:outline-none focus-within:ring-2',
  isInvalid.value
    ? 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500'
    : 'border-gray-300 hover:border-gray-400 focus-within:border-accent-600 focus-within:ring-accent-600',
])

const AFFIX = 'flex items-center bg-gray-100 px-3 text-sm font-medium text-gray-700 shrink-0'

/**
 * In affix mode a caller's `class` sizes the whole group (e.g. max-w-xs), so it
 * belongs on the wrapper rather than on the inner borderless input. In the
 * plain case there is no wrapper and Vue merges it with the control classes.
 */
const groupRootClass = computed(() => attrs.class as unknown)
const affixInputAttrs = computed(() => {
  const { class: _group, ...rest } = attrs
  return rest
})

function onInput(event: Event) {
  const el = event.target as HTMLInputElement
  // Number inputs emit numbers so callers do not have to coerce. An empty field
  // emits '' rather than NaN, which is what form state expects for "not set".
  if (props.type === 'number' || props.modelModifiers.number) {
    emit('update:modelValue', el.value === '' ? '' : Number(el.value))
    return
  }
  emit('update:modelValue', props.modelModifiers.trim ? el.value.trim() : el.value)
}

/**
 * A template ref on this component yields the component instance, not the DOM
 * node, so a caller doing `inputRef.value?.focus()` needs it forwarded explicitly.
 */
const inputEl = ref<HTMLInputElement | null>(null)
defineExpose({ focus: () => inputEl.value?.focus() })
</script>

<template>
  <!--
    Two branches rather than one wrapper for every input: the plain case keeps
    the <input> as the direct child, so existing layouts that target it are
    unaffected by the affix support.
  -->
  <div v-if="hasAffix" :class="[groupClasses, groupRootClass]">
    <span v-if="$slots.prefix" :class="[AFFIX, 'border-r border-gray-300']">
      <slot name="prefix" />
    </span>
    <input
      ref="inputEl"
      :id="controlId"
      :type="type"
      :value="modelValue"
      :aria-invalid="isInvalid || undefined"
      :aria-describedby="field?.describedBy.value"
      :class="[
        CONTROL_SIZE[size],
        'h-full w-full min-w-0 bg-white text-gray-900 outline-none placeholder:text-gray-400',
        'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
      ]"
      @input="onInput"
      v-bind="affixInputAttrs"
    />
    <span v-if="$slots.suffix" :class="[AFFIX, 'border-l border-gray-300']">
      <slot name="suffix" />
    </span>
  </div>

  <input
    v-else
    ref="inputEl"
    :id="controlId"
    :type="type"
    :value="modelValue"
    :aria-invalid="isInvalid || undefined"
    :aria-describedby="field?.describedBy.value"
    :class="isBare ? BARE_CONTROL : controlClasses(size, isInvalid, { ownWidth, ownHeight })"
    @input="onInput"
    v-bind="$attrs"
  />
</template>
